import { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  Code,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  HorizontalRule,
  Undo,
  Redo,
  Title,
  Link as LinkIcon,
  LinkOff,
  Image as ImageIcon,
} from "@mui/icons-material";
import { brand } from "@/theme";

// StarterKit ships its own Link, so we need to disable the bundled one
// to avoid the "duplicate extension" warning when we explicitly add
// the configurable Link extension below.
const extensions = [
  StarterKit.configure({
    // StarterKit's bundled link doesn't expose openOnClick; we want
    // tiptap's Link extension directly so links are clickable in preview
    // and editable inline.
    link: false,
  }),
  Placeholder.configure({ placeholder: "Start writing..." }),
  Typography,
  Link.configure({
    openOnClick: true,
    HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
  }),
  Image.configure({
    HTMLAttributes: { style: "max-width: 100%; height: auto;" },
  }),
];

interface TipTapEditorProps {
  content: string;
  onChange: (html: string, markdown: string) => void;
}

function ToolbarButton({
  icon,
  title,
  active,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  active?: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            borderRadius: 1,
            bgcolor: active ? `${brand.forestGreen}20` : "transparent",
            color: active ? brand.forestGreen : "text.secondary",
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default function TipTapEditor({ content, onChange }: TipTapEditorProps) {
  const editor = useEditor({
    extensions,
    content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText();
      onChange(html, text);
    },
  });

  // Update editor content when prop changes (e.g. after async data load)
  useEffect(() => {
    if (editor && content && editor.getHTML() !== content) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  const [linkDialog, setLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [imageDialog, setImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");

  if (!editor) return null;

  const openLinkDialog = () => {
    const existing = editor.getAttributes("link").href as string | undefined;
    setLinkUrl(existing ?? "");
    setLinkDialog(true);
  };
  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
    setLinkDialog(false);
    setLinkUrl("");
  };
  const openImageDialog = () => {
    setImageUrl("");
    setImageAlt("");
    setImageDialog(true);
  };
  const applyImage = () => {
    const url = imageUrl.trim();
    if (url) {
      editor
        .chain()
        .focus()
        .setImage({ src: url, alt: imageAlt || undefined })
        .run();
    }
    setImageDialog(false);
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.25,
          p: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "#fafafa",
        }}
      >
        <ToolbarButton
          icon={<Title fontSize="small" />}
          title="Heading 1"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        />
        <ToolbarButton
          icon={<Title sx={{ fontSize: 16 }} />}
          title="Heading 2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton
          icon={<FormatBold fontSize="small" />}
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          icon={<FormatItalic fontSize="small" />}
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          icon={<FormatStrikethrough fontSize="small" />}
          title="Strikethrough"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
        <ToolbarButton
          icon={<Code fontSize="small" />}
          title="Inline code"
          active={editor.isActive("code")}
          onClick={() => editor.chain().focus().toggleCode().run()}
        />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton
          icon={<FormatListBulleted fontSize="small" />}
          title="Bullet list"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          icon={<FormatListNumbered fontSize="small" />}
          title="Numbered list"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolbarButton
          icon={<FormatQuote fontSize="small" />}
          title="Quote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <ToolbarButton
          icon={<HorizontalRule fontSize="small" />}
          title="Horizontal rule"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton
          icon={<LinkIcon fontSize="small" />}
          title="Add or edit link"
          active={editor.isActive("link")}
          onClick={openLinkDialog}
        />
        <ToolbarButton
          icon={<LinkOff fontSize="small" />}
          title="Remove link"
          disabled={!editor.isActive("link")}
          onClick={() => editor.chain().focus().unsetLink().run()}
        />
        <ToolbarButton
          icon={<ImageIcon fontSize="small" />}
          title="Insert image"
          onClick={openImageDialog}
        />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton
          icon={<Undo fontSize="small" />}
          title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
        />
        <ToolbarButton
          icon={<Redo fontSize="small" />}
          title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
        />
      </Box>

      {/* Editor content */}
      <Box
        sx={{
          "& .tiptap": {
            minHeight: 300,
            p: 2,
            outline: "none",
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: "1rem",
            lineHeight: 1.7,
            "& h1": { fontFamily: "'Playfair Display', serif", fontSize: "1.8rem", mt: 2, mb: 1 },
            "& h2": { fontFamily: "'Playfair Display', serif", fontSize: "1.4rem", mt: 2, mb: 1 },
            "& h3": {
              fontFamily: "'Playfair Display', serif",
              fontSize: "1.2rem",
              mt: 1.5,
              mb: 0.5,
            },
            "& p": { mb: 1 },
            // Explicit list styles — without these, Tailwind/MUI's CSS reset
            // strips the bullet/number markers and lists look like plain
            // indented paragraphs. Closes Evelina's bug: "bullet points and
            // numbering doe't work in this article creation box".
            "& ul": { pl: 4, mb: 1, listStyleType: "disc" },
            "& ol": { pl: 4, mb: 1, listStyleType: "decimal" },
            "& ul ul": { listStyleType: "circle" },
            "& ul ul ul": { listStyleType: "square" },
            "& li": { display: "list-item", mb: 0.25 },
            "& li > p": { mb: 0 },
            // Link styling — make hyperlinks actually look clickable.
            "& a": {
              color: brand.forestGreen,
              textDecoration: "underline",
              cursor: "pointer",
              "&:hover": { color: brand.warmGold },
            },
            // Images — constrain to container width by default.
            "& img": {
              maxWidth: "100%",
              height: "auto",
              borderRadius: 4,
              my: 1,
            },
            "& blockquote": {
              borderLeft: `3px solid ${brand.warmGold}`,
              pl: 2,
              ml: 0,
              color: "text.secondary",
              fontStyle: "italic",
            },
            "& code": {
              bgcolor: "#f4f3ec",
              px: 0.5,
              borderRadius: 0.5,
              fontFamily: "monospace",
              fontSize: "0.9em",
            },
            "& pre": {
              bgcolor: "#1a1a1a",
              color: "#f0f0f0",
              p: 2,
              borderRadius: 1,
              overflow: "auto",
              "& code": { bgcolor: "transparent", px: 0, color: "inherit" },
            },
            "& hr": { border: "none", borderTop: "1px solid", borderColor: "divider", my: 2 },
            "& .is-editor-empty:first-of-type::before": {
              content: "attr(data-placeholder)",
              color: "#adb5bd",
              float: "left",
              height: 0,
              pointerEvents: "none",
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      <Dialog open={linkDialog} onClose={() => setLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add or edit link</DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <TextField
            autoFocus
            fullWidth
            label="URL"
            placeholder="https://elitemind.co.uk"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyLink}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imageDialog} onClose={() => setImageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert image</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}
        >
          <TextField
            autoFocus
            fullWidth
            label="Image URL"
            placeholder="https://… or a Google Drive/Photos shareable link"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            helperText="Paste a URL pointing at an image. File-upload-to-KB coming in a follow-up."
          />
          <TextField
            fullWidth
            label="Alt text (for accessibility)"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyImage} disabled={!imageUrl.trim()}>
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
